import { useState, type ReactNode } from 'react'
import { ShieldAlert, Check, AlertTriangle, ArrowRight, ArrowLeft, RotateCcw, Code2, ChevronDown, ChevronUp } from 'lucide-react'
import type { AnyObj, ChecklistItem } from '../types'
import { StageGuidedShell } from './StageGuidedShell'
import type { GuidedStep } from './StageGuidedShell'
import { AML_CHECKLIST_ICONS, AML_CHECKLIST_DESCRIPTIONS } from './amlExperienceShared'

interface AmlGuidedViewProps {
  workflowResult: AnyObj | null
  amlStep: AnyObj | null
  riskFactors: AnyObj[]
  customerName: string | null
  checklistItems: ChecklistItem[]
  checklistEnabled: Record<string, boolean>
  onToggleChecklistItem: (id: string) => void
  technicalDetails: ReactNode
}

export function AmlGuidedView({
  workflowResult, amlStep, riskFactors, customerName, checklistItems, checklistEnabled, onToggleChecklistItem, technicalDetails,
}: AmlGuidedViewProps) {
  const [showTechnical, setShowTechnical] = useState(false)
  const clear = amlStep?.result === 'PASS' || amlStep?.result === 'CLEAR'
  const name = customerName ?? 'this customer'

  const steps: GuidedStep[] = [
    {
      label: 'Overview',
      render: ({ next }) => (
        <div className="rounded-[18px] p-8 text-center" style={{ background: 'linear-gradient(135deg, rgba(99,102,241,.12), rgba(167,139,250,.06))', border: '1px solid rgba(99,102,241,.25)' }}>
          <div className="w-16 h-16 rounded-[18px] flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(99,102,241,.18)' }}>
            <ShieldAlert size={30} className="text-[#4f46e5]" />
          </div>
          <h2 className="text-[22px] font-bold text-[#1e293b]">Watchlist & Media Screening</h2>
          <p className="text-[14px] text-[#4f46e5] mt-2 max-w-md mx-auto leading-relaxed">
            Screen {name} against sanctions lists, politically-exposed-person databases, and negative news —
            automatically, as part of the same identity check.
          </p>
          <button
            onClick={next}
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-[10px] text-[14px] font-semibold text-white transition-all"
            style={{ background: '#6366f1', boxShadow: '0 4px 16px rgba(99,102,241,.5)' }}
          >
            Get started <ArrowRight size={15} />
          </button>
        </div>
      ),
    },
    {
      label: 'What to include',
      render: ({ next, back }) => (
        <div className="space-y-4">
          <div>
            <h3 className="text-[16px] font-semibold text-[#1e293b]">What should be screened?</h3>
            <p className="text-[12px] text-[#475569] mt-1">Tap anything that isn&apos;t needed for this customer to leave it out.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {checklistItems.map((item) => {
              const Icon = AML_CHECKLIST_ICONS[item.id] ?? ShieldAlert
              const active = checklistEnabled[item.id] ?? true
              return (
                <button
                  key={item.id}
                  onClick={() => onToggleChecklistItem(item.id)}
                  className="text-left rounded-[14px] p-4 transition-all"
                  style={active
                    ? { background: '#f8fafc', border: '1px solid rgba(99,102,241,.35)' }
                    : { background: '#f8fafc', border: '1px solid #e5e7eb', opacity: 0.5 }
                  }
                >
                  <Icon size={20} className={active ? 'text-[#4f46e5]' : 'text-[#475569]'} />
                  <p className="text-[13px] font-semibold text-[#1e293b] mt-2">{item.label}</p>
                  <p className="text-[12px] text-[#475569] mt-1 leading-snug">{AML_CHECKLIST_DESCRIPTIONS[item.id] ?? ''}</p>
                </button>
              )
            })}
          </div>
          <div className="flex justify-between pt-2">
            <button onClick={back} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[9px] text-[13px] font-medium text-[#475569]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
              <ArrowLeft size={13} /> Back
            </button>
            <button onClick={next} className="inline-flex items-center gap-1.5 px-5 py-2 rounded-[9px] text-[13px] font-semibold text-white" style={{ background: '#6366f1' }}>
              Next <ArrowRight size={13} />
            </button>
          </div>
        </div>
      ),
    },
    {
      label: 'Result',
      render: ({ back, restart }) => (
        <div className="space-y-4">
          <div className="rounded-[18px] overflow-hidden" style={{ border: '1px solid #e5e7eb', background: '#ffffff' }}>
            {!workflowResult ? (
              <div className="p-6 text-center">
                <p className="text-[13px] text-[#475569]">Run the Identity + Background Check on the KYC stage first — this screening happens automatically as part of that same check.</p>
              </div>
            ) : !amlStep ? (
              <div className="p-6 text-center">
                <p className="text-[13px] text-[#475569]">This particular workflow didn&apos;t include a screening step.</p>
              </div>
            ) : (
              <div className="p-5 flex items-center gap-4" style={{ background: clear ? 'rgba(52,211,153,.06)' : 'rgba(251,146,60,.06)' }}>
                <div className="w-11 h-11 rounded-[10px] flex items-center justify-center shrink-0" style={{ background: clear ? 'rgba(52,211,153,.15)' : 'rgba(251,146,60,.15)' }}>
                  {clear ? <Check size={22} className="text-emerald-700" /> : <AlertTriangle size={22} className="text-amber-700" />}
                </div>
                <div>
                  <p className="text-[14px] font-semibold text-[#1e293b]">
                    {clear ? `No hits — ${name} is clear.` : `${riskFactors.length} potential match${riskFactors.length !== 1 ? 'es' : ''} found for ${name}.`}
                  </p>
                  {riskFactors.length > 0 && (
                    <p className="text-[12px] text-[#475569] mt-1">{riskFactors.map((f) => f.description ?? f.factor).join(', ')}</p>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="rounded-[14px] overflow-hidden" style={{ border: '1px solid #e5e7eb' }}>
            <button
              onClick={() => setShowTechnical((s) => !s)}
              className="w-full flex items-center justify-between px-4 py-3 transition-colors hover:bg-[#ffffff]"
              style={{ background: '#f8fafc' }}
            >
              <span className="flex items-center gap-2 text-[12px] font-semibold text-[#475569]">
                <Code2 size={13} /> See how this works technically
              </span>
              {showTechnical ? <ChevronUp size={13} className="text-[#64748b]" /> : <ChevronDown size={13} className="text-[#64748b]" />}
            </button>
            {showTechnical && <div className="p-4 space-y-3" style={{ background: '#ffffff' }}>{technicalDetails}</div>}
          </div>

          <div className="flex justify-between pt-2">
            <button onClick={back} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[9px] text-[13px] font-medium text-[#475569]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
              <ArrowLeft size={13} /> Back
            </button>
            <button onClick={restart} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[9px] text-[13px] font-medium text-[#475569]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
              <RotateCcw size={13} /> Start over
            </button>
          </div>
        </div>
      ),
    },
  ]

  return <StageGuidedShell steps={steps} />
}
